import { memo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"

import { IssueCompletedIcon, IssueUnstartedIcon } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import {
	agentHomeTodos,
	chatsLabel,
	splitHomeRows,
} from "./agent-home-todos-model"
import { agentPath } from "./agent-route"
import { closedIdeaLabel } from "./format"
import { FADE_OUT_LABEL, ideaTitle } from "./idea-list"
import { useAgentItems } from "./use-agent-recommendations"

// A teammate's decision older than this is no longer news on the home.
const RECENTLY_CLOSED_MS = 7 * 24 * 60 * 60 * 1000

const CARD =
	"w-full overflow-hidden rounded-[10px] bg-white text-left shadow-[0_1px_3px_rgba(0,0,0,0.06)] ring-1 ring-black/[0.08] dark:bg-gray-800 dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)] dark:ring-white/10"

const ROW =
	"flex h-10 items-center gap-3 border-t border-gray-200 px-3.5 text-[13px] dark:border-gray-700/80"

export const AgentHomeTodos = memo(() => {
	const { items } = useAgentItems()
	const [expanded, setExpanded] = useState(false)
	const [closed] = useQuery(queries.lastClosedAgentRecommendation())
	const [members] = useQuery(queries.teamMembers())
	const todos = agentHomeTodos(items, ideaTitle)
	const recentClosed =
		closed && Date.now() - closed.updatedAt < RECENTLY_CLOSED_MS ? closed : null
	if (todos.length === 0 && !recentClosed) return null
	const closedBy = members.find(
		(member) => member.user?.id === recentClosed?.closedByUserId,
	)?.user?.name

	const rows = [
		...todos.map((todo) => ({
			key: todo.id,
			to: agentPath("all", todo.id),
			testId: "agent-home-todo",
			title: todo.title,
			chats: todo.chats,
		})),
	]
	const { head, rest } = splitHomeRows(rows)

	const renderRow = (row: (typeof rows)[number]) => (
		<Link
			key={row.key}
			to={row.to}
			data-testid={row.testId}
			className={`${ROW} text-primary hover:bg-gray-50 dark:hover:bg-gray-750`}
		>
			<IssueUnstartedIcon className="h-3.5 w-3.5 flex-none transform-gpu text-gray-400 dark:text-gray-500" />
			<span className={FADE_OUT_LABEL}>{row.title}</span>
			{row.chats !== null && (
				<span className="flex-none text-xs text-tertiary">
					{chatsLabel(row.chats)}
				</span>
			)}
		</Link>
	)

	const doneRow = recentClosed && (
		<div
			data-testid="agent-home-done"
			className={`${ROW} bg-gray-50 text-tertiary dark:bg-gray-850`}
		>
			<IssueCompletedIcon className="h-3.5 w-3.5 flex-none text-gray-400 dark:text-gray-500" />
			<span className={`${FADE_OUT_LABEL} line-through`}>
				{recentClosed.title}
			</span>
			<span className="flex-none text-xs">
				{closedIdeaLabel(recentClosed.status, closedBy)}
			</span>
		</div>
	)

	return (
		<div data-testid="agent-home-todos" className={`${CARD} max-w-[26rem]`}>
			<div className="flex h-10 items-center justify-between px-3.5">
				<span className="text-[13px] font-medium text-primary">To do</span>
				<span className="text-xs text-tertiary">{rows.length} open</span>
			</div>
			{head.map(renderRow)}
			{rest.length === 0 ? (
				doneRow
			) : (
				<>
					{/* Stays mounted so the rows open with a transition instead of popping in. */}
					<div
						data-testid="agent-home-more"
						data-expanded={expanded}
						inert={!expanded}
						className={`ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows] duration-200 motion-reduce:transition-none ${expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
					>
						<div className="min-h-0 overflow-hidden">
							<div
								className={`ease-[cubic-bezier(0.23,1,0.32,1)] max-h-[280px] overflow-y-auto transition-opacity duration-200 ${expanded ? "opacity-100" : "opacity-0"}`}
							>
								{rest.map(renderRow)}
								{doneRow}
							</div>
						</div>
					</div>
					<button
						type="button"
						data-testid="agent-home-show-more"
						aria-expanded={expanded}
						onClick={() => setExpanded((value) => !value)}
						className={`${ROW} w-full text-tertiary hover:bg-gray-50 hover:text-primary dark:hover:bg-gray-750`}
					>
						{expanded ? "Show less" : "Show more"}
					</button>
				</>
			)}
		</div>
	)
})
